'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { offlineDB, type OfflineVerification } from '@/lib/offline-db';

const PENDING_CHECK_INTERVAL = 60_000; // 1 minute - refresh pending count
const SYNC_SETTINGS_REFRESH_INTERVAL = 5 * 60_000; // 5 minutes - refresh sync settings

interface UseOfflineSyncReturn {
  isOnline: boolean;
  lastSyncTime: string | null;
  syncCount: number;
  pendingCount: number;
  syncEnabled: boolean;
  syncFrequencyMinutes: number;
  syncNow: () => Promise<{ success: boolean; syncedCount: number; failedCount: number }>;
  refreshSyncSettings: () => Promise<void>;
}

export function useOfflineSync(): UseOfflineSyncReturn {
  const {
    isOnline, setIsOnline,
    lastSyncTime, setLastSyncTime,
    syncCount, setSyncCount,
    pendingSyncCount, setPendingSyncCount,
    syncEnabled, setSyncEnabled,
    syncFrequencyMinutes, setSyncFrequencyMinutes,
  } = useAppStore();
  const { token, user, isAuthenticated } = useAuthStore();

  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncSettingsRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncingRef = useRef(false);
  // Ref to always hold the latest syncNow function for use in intervals/callbacks
  const syncNowRef = useRef<() => Promise<{ success: boolean; syncedCount: number; failedCount: number }>>();

  // Fetch effective sync settings from server
  const fetchSyncSettings = useCallback(async () => {
    const currentToken = token;
    if (!isAuthenticated || !currentToken) return;

    try {
      const response = await fetch('/api/officer/sync-settings', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.settings) {
          setSyncEnabled(result.settings.syncEnabled);
          setSyncFrequencyMinutes(result.settings.syncFrequencyMinutes);
          console.log(`[Sync] Settings updated: enabled=${result.settings.syncEnabled}, frequency=${result.settings.syncFrequencyMinutes}min`);
        }
      } else {
        console.warn('[Sync] Failed to fetch sync settings, keeping current values');
      }
    } catch {
      // Offline or network error - keep cached values from app store
      console.warn('[Sync] Cannot reach sync settings API, using cached values');
    }
  }, [isAuthenticated, token, setSyncEnabled, setSyncFrequencyMinutes]);

  // Refresh pending count from IndexedDB
  const refreshPendingCount = useCallback(async () => {
    const boothId = user?.boothId;
    if (!boothId) return;
    try {
      await offlineDB.init();
      const count = await offlineDB.getUnsyncedCount(boothId);
      setPendingSyncCount(count);
    } catch (error) {
      console.error('Failed to refresh pending count:', error);
    }
  }, [user?.boothId, setPendingSyncCount]);

  // Sync pending verifications to the server
  // Manual sync (from button press) ALWAYS works regardless of syncEnabled
  const syncNow = useCallback(async (): Promise<{ success: boolean; syncedCount: number; failedCount: number }> => {
    const boothId = user?.boothId;
    const currentToken = token;

    if (!isAuthenticated || !currentToken || !boothId) {
      console.warn('Cannot sync: not authenticated or no booth assigned');
      return { success: false, syncedCount: 0, failedCount: 0 };
    }

    // Prevent concurrent syncs
    if (syncingRef.current) {
      console.log('Sync already in progress, skipping');
      return { success: false, syncedCount: 0, failedCount: 0 };
    }

    syncingRef.current = true;

    try {
      // Get unsynced verifications from IndexedDB
      const unsyncedVerifications = await offlineDB.getUnsyncedVerifications(boothId);

      if (unsyncedVerifications.length === 0) {
        setPendingSyncCount(0);
        syncingRef.current = false;
        return { success: true, syncedCount: 0, failedCount: 0 };
      }

      console.log(`[Sync] Found ${unsyncedVerifications.length} unsynced verifications for booth ${boothId}`);

      // Prepare payload - send in batches of 5 to avoid large request bodies
      const BATCH_SIZE = 5;
      let totalSynced = 0;
      let totalFailed = 0;

      for (let i = 0; i < unsyncedVerifications.length; i += BATCH_SIZE) {
        const batch = unsyncedVerifications.slice(i, i + BATCH_SIZE);

        const payload = batch.map((v: OfflineVerification) => ({
          id: v.id,
          epicNo: v.epicNo,
          serialNo: v.serialNo,
          voterName: v.voterName,
          fatherHusbandName: v.fatherHusbandName,
          relationType: v.relationType,
          gender: v.gender,
          age: v.age,
          signatureImage: v.signatureImage,
          thumbImage: v.thumbImage,
          verificationType: v.verificationType,
          pollingBoothId: v.pollingBoothId,
          officerId: v.officerId,
          officerName: v.officerName,
          verifiedAt: v.verifiedAt,
        }));

        try {
          const response = await fetch('/api/officer/sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
            body: JSON.stringify({ verifications: payload }),
          });

          if (response.ok) {
            const result = await response.json();

            // Mark synced verifications in IndexedDB
            const syncedIds = batch.map((v: OfflineVerification) => v.id);
            await offlineDB.markVerificationsSynced(syncedIds);

            // Remove from sync queue
            const syncQueue = await offlineDB.getSyncQueue();
            const queueIdsToRemove = syncQueue
              .filter((sq) => syncedIds.includes(sq.data.id))
              .map((sq) => sq.id);
            if (queueIdsToRemove.length > 0) {
              await offlineDB.removeFromSyncQueue(queueIdsToRemove);
            }

            totalSynced += result.synced || batch.length;
            console.log(`[Sync] Batch ${Math.floor(i / BATCH_SIZE) + 1}: synced ${batch.length} records`);
          } else {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error(`[Sync] Batch failed with status ${response.status}:`, errorText);
            totalFailed += batch.length;
          }
        } catch (batchError) {
          console.error('[Sync] Batch network error:', batchError);
          totalFailed += batch.length;
        }
      }

      // Update app state
      const newSyncCount = syncCount + totalSynced;
      setSyncCount(newSyncCount);

      if (totalSynced > 0) {
        setLastSyncTime(new Date().toISOString());
      }

      // Refresh pending count
      const remainingUnsynced = await offlineDB.getUnsyncedCount(boothId);
      setPendingSyncCount(remainingUnsynced);

      console.log(`[Sync] Complete: ${totalSynced} synced, ${totalFailed} failed, ${remainingUnsynced} remaining`);

      syncingRef.current = false;
      return {
        success: totalSynced > 0 || unsyncedVerifications.length === 0,
        syncedCount: totalSynced,
        failedCount: totalFailed,
      };
    } catch (error) {
      console.error('[Sync] Fatal error:', error);
      syncingRef.current = false;
      return { success: false, syncedCount: 0, failedCount: 0 };
    }
  }, [isAuthenticated, token, user?.boothId, syncCount, setSyncCount, setLastSyncTime, setPendingSyncCount]);

  // Keep the ref updated with the latest syncNow
  useEffect(() => {
    syncNowRef.current = syncNow;
  }, [syncNow]);

  // Setup or clear the auto-sync interval based on current settings
  const setupSyncInterval = useCallback(() => {
    // Clear existing interval
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    // Only setup auto-sync if sync is enabled
    if (syncEnabled && isAuthenticated) {
      const interval = syncFrequencyMinutes * 60_000;
      syncIntervalRef.current = setInterval(async () => {
        if (navigator.onLine && isAuthenticated && syncEnabled) {
          console.log(`[Sync] Auto-sync triggered (every ${syncFrequencyMinutes} min)`);
          await syncNowRef.current?.();
        }
      }, interval);
      console.log(`[Sync] Auto-sync interval set to ${syncFrequencyMinutes} minutes`);
    } else {
      console.log('[Sync] Auto-sync disabled by admin settings');
    }
  }, [syncEnabled, syncFrequencyMinutes, isAuthenticated]);

  // Setup intervals and event listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initialize offline DB
    offlineDB.init().catch(console.error);

    // Initial pending count update
    refreshPendingCount();

    // Initial sync settings fetch
    fetchSyncSettings();

    // Setup periodic sync based on settings
    setupSyncInterval();

    // Setup periodic pending count check (always runs regardless of sync settings)
    pendingCheckRef.current = setInterval(() => {
      refreshPendingCount();
    }, PENDING_CHECK_INTERVAL);

    // Setup periodic sync settings refresh (every 5 minutes)
    syncSettingsRefreshRef.current = setInterval(() => {
      fetchSyncSettings();
    }, SYNC_SETTINGS_REFRESH_INTERVAL);

    // Listen for browser online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online - respect syncEnabled setting
      if (syncEnabled) {
        console.log('[Sync] Network online - triggering auto-sync');
        setTimeout(() => syncNowRef.current?.(), 2000); // Small delay to let network stabilize
      } else {
        console.log('[Sync] Network online but auto-sync disabled by admin');
      }
      // Refresh sync settings when coming back online
      fetchSyncSettings();
    };
    const handleOffline = () => {
      setIsOnline(false);
      console.log('[Sync] Network offline');
    };

    // Listen for tab visibility changes - sync when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.onLine && syncEnabled) {
        console.log('[Sync] Tab became visible - triggering auto-sync');
        setTimeout(() => syncNowRef.current?.(), 1500);
        refreshPendingCount();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
      if (pendingCheckRef.current) clearInterval(pendingCheckRef.current);
      if (syncSettingsRefreshRef.current) clearInterval(syncSettingsRefreshRef.current);

      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [isAuthenticated, refreshPendingCount, fetchSyncSettings, setupSyncInterval, syncEnabled, setIsOnline]);

  // Re-setup sync interval when settings change
  useEffect(() => {
    if (isAuthenticated) {
      setupSyncInterval();
    }
  }, [syncEnabled, syncFrequencyMinutes, isAuthenticated, setupSyncInterval]);

  return {
    isOnline,
    lastSyncTime,
    syncCount,
    pendingCount: pendingSyncCount,
    syncEnabled,
    syncFrequencyMinutes,
    syncNow,
    refreshSyncSettings: fetchSyncSettings,
  };
}
